import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { FC, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaXmark } from "react-icons/fa6";
import { TfiHeadphoneAlt } from "react-icons/tfi";
import { TiThMenu } from "react-icons/ti";
import { CartButtonWithSwipeableDrawer } from "../../default";
import { HeaderNavLinkItem } from "../../header-menu-utils";

interface DefaultV2ResponsiveHeaderNavProps {
  items: HeaderNavLinkItem[];
  hotline?: string;
  cartUrl?: string;
  checkoutUrl?: string;
  productUrlPrefix?: string;
}

export const DefaultV2ResponsiveHeaderNav: FC<DefaultV2ResponsiveHeaderNavProps> = (props) => {
  const theme = useTheme();
  const pathname = usePathname();
  const [openMenu, setOpenMenu] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const isLinkActive = useCallback(
    (url?: string) => {
      if (!url) return false;
      return pathname === url || (url === "/" && pathname === "/");
    },
    [pathname],
  );

  const closeMenu = useCallback(() => setOpenMenu(false), []);

  const toggleMenu = useCallback(() => {
    setOpenMenu((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!openMenu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openMenu, closeMenu]);

  useEffect(() => {
    if (!openMenu) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [openMenu]);

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key],
    );
  };

  const menuLayer =
    openMenu &&
    typeof document !== "undefined" &&
    createPortal(
      <>
        <Box
          role="presentation"
          onClick={closeMenu}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: (t) => t.zIndex.modal,
            bgcolor: alpha(theme.palette.common.black, 0.52),
          }}
        />
        <Box
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            width: "min(72vw, 360px)",
            maxWidth: "90vw",
            zIndex: (t) => t.zIndex.modal + 1,
            bgcolor: "background.paper",
            display: "flex",
            flexDirection: "column",
            boxShadow: theme.shadows[16],
            overflowY: "auto",
          }}
        >
          <IconButton
            type="button"
            aria-label="close"
            sx={{ alignSelf: "flex-end", mt: 0.5, mr: 0.5 }}
            onClick={closeMenu}
          >
            <FaXmark size={30} />
          </IconButton>

          <List component="nav" sx={{ width: "100%", py: 0 }}>
            {props.items.map((item) => {
              const hasSubmenu = !!item.subs?.length;
              const isItemActive = hasSubmenu
                ? item.subs!.some((sub) => isLinkActive(sub.url))
                : isLinkActive(item.url);
              const isExpanded = expandedKeys.includes(item.key);

              return (
                <Box key={item.key}>
                  <ListItemButton
                    component={!hasSubmenu && item.url ? Link : "div"}
                    href={!hasSubmenu && item.url ? item.url : undefined}
                    onClick={(e: React.MouseEvent) => {
                      if (hasSubmenu) {
                        e.preventDefault();
                        toggleExpanded(item.key);
                      } else {
                        closeMenu();
                      }
                    }}
                    sx={{
                      paddingX: 4,
                      paddingY: 2,
                      fontWeight: isItemActive ? 800 : 700,
                      backgroundColor: isItemActive ? "primary.main" : "transparent",
                      color: isItemActive ? "white" : "inherit",
                      "&:hover": {
                        backgroundColor: "primary.light",
                      },
                    }}
                  >
                    <ListItemText primary={item.label} />
                  </ListItemButton>

                  {hasSubmenu && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.subs!.map((sub) => {
                          const isSubActive = isLinkActive(sub.url);
                          return (
                            <ListItemButton
                              key={sub.key}
                              component={Link}
                              href={sub.url}
                              onClick={closeMenu}
                              sx={{
                                pl: 8,
                                py: 1.5,
                                fontWeight: isSubActive ? 800 : 700,
                                backgroundColor: isSubActive ? "primary.main" : "transparent",
                                color: isSubActive ? "white" : "inherit",
                                "&:hover": {
                                  backgroundColor: "primary.light",
                                },
                              }}
                            >
                              <ListItemText primary={sub.label} />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </List>
        </Box>
      </>,
      document.body,
    );

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ touchAction: "manipulation" }}>
        <CartButtonWithSwipeableDrawer
          cartUrl={props.cartUrl}
          checkoutUrl={props.checkoutUrl}
          productUrlPrefix={props.productUrlPrefix}
        />
        {props.hotline && (
          <IconButton
            type="button"
            color="primary"
            aria-label="call"
            onClick={() => {
              window.location.href = `tel:${props.hotline}`;
            }}
            sx={{ padding: { xs: 0.75, sm: 1 } }}
          >
            <TfiHeadphoneAlt size={20} />
          </IconButton>
        )}
        <IconButton
          type="button"
          aria-label="menu"
          aria-expanded={openMenu}
          onClick={toggleMenu}
          sx={{
            padding: { xs: 0.75, sm: 1 },
            minWidth: 44,
            minHeight: 44,
            color: "primary.main",
            "& svg": { pointerEvents: "none" },
          }}
        >
          <TiThMenu size={24} />
        </IconButton>
      </Stack>

      {menuLayer}
    </>
  );
};

DefaultV2ResponsiveHeaderNav.displayName = "DefaultV2ResponsiveHeaderNav";
